import { AfterViewInit, Component, Input, OnChanges } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { PoreFrac } from 'src/app/perivis/models/poreFrac.model';
import { LinearInterpSingle } from 'src/app/perivis/shared/helpers/linear-interp-single.helper';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';
import { InternalPressureProfileType, LostReturnsWaterIPP } from '../../models/internal-pressure-profile.model';
import { UomQuantityInput } from '../../uom-quantity-input/uom-quantity-input-component';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { lastValueFrom } from 'rxjs';
import { GetValueFromPpg } from 'src/app/perivis/shared/helpers/units.helper';

interface LostReturnsWaterIppForm {
  type: InternalPressureProfileType.LOSTRETURNSWATER;
  fractureDepth: UomQuantityInput;
  fractureGradient: UomQuantityInput;
  fluidDensity: UomQuantityInput;
  fractureMargin: UomQuantityInput;
}

@Component({
    selector: 'app-lost-returns-water-ipp',
    templateUrl: './lost-returns-water-ipp.component.html',
    providers: subformComponentProviders(LostReturnsWaterIppComponent),
    standalone: false
})
export class LostReturnsWaterIppComponent implements AfterViewInit, OnChanges {

  @Input() currentString: any;
  @Input() fracPressures: PoreFrac[];
  @Input() userUnits: UserUnitsModel;
  @Input() influxDepthRange: { min: number, max: number };

  public validators: any;
  public defaultFracGradient: number;
  public densityLabel: string;
  public densityValidation: { min: number, max: number };
  public fractureGradientValidation: { min: number, max: number };
  public marginOfErrorValidation: { min: number, max: number };

  public form = createForm<LostReturnsWaterIPP, LostReturnsWaterIppForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(InternalPressureProfileType.LOSTRETURNSWATER),
      fractureDepth: new UntypedFormControl(null),
      fractureGradient: new UntypedFormControl(null),
      fluidDensity: new UntypedFormControl(null),
      fractureMargin: new UntypedFormControl(null)
    },
    toFormGroup: (obj: LostReturnsWaterIPP | null): LostReturnsWaterIppForm | null => {
      if (!obj) {
        return null;
      }

      return {
        type: obj.type,
        fractureDepth: { control: obj.fractureDepth },
        fractureGradient: { control: obj.fractureGradient },
        fluidDensity: { control: obj.fluidDensity },
        fractureMargin: { control: obj.fractureMargin }
      }
    },
    fromFormGroup: (formValue: LostReturnsWaterIppForm): LostReturnsWaterIPP | null => {
      const { fractureDepth, fractureGradient, fluidDensity, fractureMargin, ...commonValues } = formValue;
      return {
        fractureDepth: +fractureDepth?.control,
        fractureGradient: +fractureGradient?.control,
        fluidDensity: +fluidDensity?.control,
        fractureMargin: +fractureMargin?.control,
        ...commonValues
      }
    }
  })

  constructor(private _trajectoryService: TrajectoryService) { }

  ngAfterViewInit(): void {
    this.getEmwDefaultFrac(this.influxDepthRange.min);
  }

  async ngOnChanges(): Promise<void> {
    this.densityLabel = unitsLib[this.userUnits.density].symbol;

    switch (this.densityLabel) {
      case 'ppg':
        this.densityValidation = { min: 0.01, max: 25 };
        this.marginOfErrorValidation = { min: 0, max: 10 };
        this.fractureGradientValidation = { min: 1, max: 25 };
        break;
      case 'kg/m³':
      case 'g/L':
        this.densityValidation = { min: 1, max: 2500 };
        this.marginOfErrorValidation = { min: 0, max: 1000 };
        this.fractureGradientValidation = { min: 119.8, max: 2995 };
        break;
      case 'g/cm³':
      case 'kg/l':
      case 'sg':
        this.densityValidation = { min: 0.00119, max: 3 };
        this.marginOfErrorValidation = { min: 0, max: 1.2 };
        this.fractureGradientValidation = { min: 0.119, max: 3 };
        break;
      case 'g/m³':
        this.densityValidation = { min: 1198, max: 2995660 };
        this.marginOfErrorValidation = { min: 0, max: 1198264 };
        this.fractureGradientValidation = { min: 119826, max: 2995660 };
        break;
    }

    this.validators = {
      fractureDepth: [Validators.min(this.influxDepthRange.min), Validators.max(this.influxDepthRange.max), Validators.required],
      fractureGradient: [Validators.min(this.fractureGradientValidation.min), Validators.max(this.fractureGradientValidation.max), Validators.required],
      fluidDensity: [Validators.min(this.densityValidation.min), Validators.max(this.densityValidation.max), Validators.required],
      fractureMargin: [Validators.min(this.marginOfErrorValidation.min), Validators.max(this.marginOfErrorValidation.max), Validators.required]
    }

      await this.getEmwDefaultFrac(this.influxDepthRange.min);
  }

  async getEmwDefaultFrac(depth, updateFracGradient = false): Promise<void> {
    const tvds = await lastValueFrom(this._trajectoryService.getTvdsFromMds([depth]));
    if (this.fracPressures?.length > 1) {
      if (depth < this.currentString?.hangerMd) {
        return;
      }
      this.defaultFracGradient = +LinearInterpSingle(tvds[0], this.fracPressures, this.userUnits).toFixed(2);
    } else {
      this.defaultFracGradient = this.currentString.annularFluid?.state.nominalDensity + GetValueFromPpg(2, this.densityLabel);
    }
    if (!this.form.formGroup.controls.fractureGradient.value?.control || updateFracGradient) {
      this.form.formGroup.controls.fractureGradient.setValue({ control: this.defaultFracGradient });
    }

    if (!this.form.formGroup.controls.fluidDensity.value?.control) {
      const defaultFluidDensity = GetValueFromPpg(8.33, unitsLib[this.userUnits.density].symbol);
      this.form.formGroup.controls.fluidDensity.setValue({ control: defaultFluidDensity });
    }
  }
  
    public fractureDepthChange() {
    if (this.form.formGroup.controls.fractureDepth.value?.control) {
      this.getEmwDefaultFrac(this.form.formGroup.controls.fractureDepth.value.control, true);
    }
  }

  public fractureGradientChange() {
    if (!this.form.formGroup.controls.fractureGradient.value?.control) {
      this.getEmwDefaultFrac(this.form.formGroup.controls.fractureDepth.value.control, true);
    }
  }
}
